import { createSelector, createAsyncThunk, createSlice, unwrapResult } from '@reduxjs/toolkit';
import { useState, useCallback, useMemo, useReducer, useRef, useEffect } from 'react';
import '@discngine/moosa-models';
import 'lodash/upperFirst';

const selectState = (path) => (state) => state[path];
const selectIds = (path) => createSelector(selectState(path), (state) => state.ids);
const selectItems = (path) => createSelector(selectState(path), (state) => state.items);
const selectResult = (path) => createSelector(selectIds(path), selectItems(path), (ids, items) => ids.map((id) => items[id]));
const selectItemById = (path, id) => createSelector(selectItems(path), (items) => id && id in items ? items[id] : undefined);
const selectTotal = (path) => createSelector(selectState(path), (state) => state.total);
const selectSkip = (path) => createSelector(selectState(path), (state) => state.skip);
const selectVersions = (path, parentId) => createSelector(selectState(path), (state) => parentId ? state.versions[parentId] : undefined);
const selectVersionItems = (path, parentId) => createSelector(selectVersions(path, parentId !== null && parentId !== void 0 ? parentId : undefined), selectItems(path), (children = [], items) => children.length > 0 ? children.map((id) => items[id]) : undefined);
const selectVersionItemsWithParent = (path, parentId) => createSelector(selectVersions(path, parentId !== null && parentId !== void 0 ? parentId : undefined), selectItems(path), (children = [], items) => parentId && children.length > 0 && items[parentId]
    ? [items[parentId], ...children.map((id) => items[id])]
    : undefined);

const defaultGetInitialState = () => ({
    ids: [],
    items: {},
    total: 0,
    skip: 0,
    recentlyDeleted: {},
    versions: {},
});
const createVersionisedListSlice = (params) => {
    const { sliceName, getInitialState = defaultGetInitialState } = params !== null && params !== void 0 ? params : {};
    let dataProvider = null;
    const unconfiguredDataProviderError = new Error(`DataProvider for ${sliceName} slice is not configured, you must configure it before slice usage`);
    const configureDataProvider = (configuredDataProvider) => {
        if (!dataProvider) {
            dataProvider = configuredDataProvider;
        }
    };
    const createItem = createAsyncThunk(`${sliceName}/createItem`, async ({ item }) => {
        if (!dataProvider) {
            throw unconfiguredDataProviderError;
        }
        return await dataProvider.createItem(item);
    });
    const fetchItem = createAsyncThunk(`${sliceName}/fetchItem`, async ({ id, revalidate }, { getState }) => {
        const cachedItem = selectItemById(sliceName, id)(getState());
        if (cachedItem && !revalidate) {
            return cachedItem;
        }
        if (!dataProvider) {
            throw unconfiguredDataProviderError;
        }
        return await dataProvider.getItem(id);
    });
    const updateItem = createAsyncThunk(`${sliceName}/updateItem`, async ({ item }) => {
        if (!dataProvider) {
            throw unconfiguredDataProviderError;
        }
        return await dataProvider.updateItem(item);
    });
    const deleteItem = createAsyncThunk(`${sliceName}/deleteItem`, async ({ id }) => {
        if (!dataProvider) {
            throw unconfiguredDataProviderError;
        }
        return await dataProvider.deleteItem(id);
    });
    const undeleteItem = createAsyncThunk(`${sliceName}/undeleteItem`, async ({ id }) => {
        if (!dataProvider) {
            throw unconfiguredDataProviderError;
        }
        return await dataProvider.undeleteItem(id);
    });
    const fetchList = createAsyncThunk(`${sliceName}/fetchList`, async ({ params: requestParams }, { getState }) => {
        const state = getState();
        if (!dataProvider) {
            throw unconfiguredDataProviderError;
        }
        return await dataProvider.getList(Object.assign({ skip: state[sliceName].skip }, requestParams));
    });
    const fetchVersions = createAsyncThunk(`${sliceName}/fetchVersions`, async ({ parentId, revalidate }, { getState }) => {
        const cachedVersions = selectVersionItems(sliceName, parentId)(getState());
        if (cachedVersions && !revalidate) {
            return {
                data: cachedVersions,
                total: cachedVersions.length,
                limit: cachedVersions.length,
                skip: 0,
            };
        }
        if (!dataProvider) {
            throw unconfiguredDataProviderError;
        }
        return await dataProvider.getVersions(parentId);
    });
    const slice = createSlice({
        name: sliceName,
        initialState: getInitialState(),
        reducers: {
            nextPage: (state, action) => {
                state.skip += action.payload;
            },
            resetState: () => {
                return getInitialState();
            },
            resetList: (state) => {
                return Object.assign(Object.assign({}, getInitialState()), { versions: state.versions, items: state.items });
            },
        },
        extraReducers: (builder) => {
            builder.addCase(fetchList.fulfilled, (state, { payload }) => {
                const map = payload.data.reduce((acc, item) => {
                    acc[item._id] = item;
                    return acc;
                }, {});
                const newIds = Object.keys(map);
                state.ids = [...state.ids.filter((id) => !newIds.includes(id)), ...newIds]; // remove items if they were loaded before loaded
                state.items = Object.assign(Object.assign({}, state.items), map);
                state.total = payload.total;
            });
            builder.addCase(fetchVersions.fulfilled, (state, { payload, meta }) => {
                state.versions[meta.arg.parentId] = payload.data.map((item) => item._id);
                payload.data.forEach((item) => {
                    state.items[item._id] = item;
                });
            });
            builder.addCase(createItem.fulfilled, (state, { payload }) => {
                var _a;
                state.items[payload._id] = payload;
                // if payload has parent ID - add to parent's versions
                if (payload.parentId) {
                    const currentVersions = (_a = state.versions[payload.parentId]) !== null && _a !== void 0 ? _a : [];
                    state.versions[payload.parentId] = currentVersions.concat(payload._id);
                    return;
                }
                // otherwise handle case if an item is last
                if (state.ids.length === state.total) {
                    state.ids.push(payload._id);
                    state.total += 1;
                }
            });
            builder.addCase(fetchItem.fulfilled, (state, { payload }) => {
                // cache item, but do not update list and total value since we do not know correct order
                state.items[payload._id] = payload;
                if (!payload.parentId) {
                    return;
                }
                // if parent record doesn't exist  - add it
                if (!state.versions[payload.parentId]) {
                    state.versions[payload.parentId] = [];
                }
                // push if version record doesn't include yet
                if (!state.versions[payload.parentId].includes(payload._id)) {
                    state.versions[payload.parentId].push(payload._id);
                }
            });
            builder.addCase(updateItem.fulfilled, (state, { payload }) => {
                var _a;
                const isNameUpdated = state.items[payload._id] && state.items[payload._id].name !== payload.name;
                // update all versions name
                if (isNameUpdated && ((_a = state.versions[payload._id]) === null || _a === void 0 ? void 0 : _a.length)) {
                    state.versions[payload._id].forEach((id) => {
                        state.items[id].name = payload.name;
                    });
                }
                // update item itself
                if (state.items[payload._id]) {
                    state.items[payload._id] = payload;
                }
            });
            builder.addCase(deleteItem.fulfilled, (state, { payload }) => {
                // cleanup parent's sate and cache for version
                if (payload.parentId && payload.parentId in state.versions) {
                    state.versions[payload.parentId] = state.versions[payload.parentId].filter((x) => x !== payload._id);
                    delete state.items[payload._id];
                    return;
                }
                // cleanup children
                if (payload._id in state.versions) {
                    state.versions[payload._id].forEach((childId) => {
                        delete state.items[childId];
                    });
                    delete state.versions[payload._id];
                }
                const idx = state.ids.indexOf(payload._id);
                delete state.items[payload._id];
                if (idx === -1) {
                    return;
                }
                // delete item
                state.ids.splice(idx, 1);
                // save idx to restore to the previous place
                state.recentlyDeleted[payload._id] = idx;
                // update cursor
                state.skip = idx;
                state.total = state.total > 0 ? state.total - 1 : 0;
            });
            builder.addCase(undeleteItem.fulfilled, (state, { payload: item }) => {
                if (item._id in state.recentlyDeleted) {
                    const idx = state.recentlyDeleted[item._id];
                    state.ids.splice(idx, 0, item._id);
                    state.items[item._id] = item;
                    state.total += 1;
                    delete state.recentlyDeleted[item._id];
                }
            });
        },
    });
    return {
        slice,
        configureDataProvider,
        // thunks
        fetchList,
        fetchVersions,
        createItem,
        fetchItem,
        updateItem,
        deleteItem,
        undeleteItem,
    };
};

const createUseCreateItemHook = (params) => {
    const { useDispatch, useSelector, slice } = params;
    const sliceName = slice.slice.name;
    const useCreateItem = () => {
        const dispatch = useDispatch();
        const [isLoading, setIsLoading] = useState(false);
        const [id, setId] = useState('');
        const item = useSelector(selectItemById(sliceName, id));
        const onCreate = useCallback(async (item) => {
            try {
                setId('');
                setIsLoading(true);
                const dispatchResult = await dispatch(slice.createItem({ item }));
                const result = await unwrapResult(dispatchResult);
                setId(result._id);
                return result;
            }
            catch (err) {
                // let the app handle error
                throw err;
            }
            finally {
                setIsLoading(false);
            }
        }, [dispatch]);
        return { onCreate, isLoading, item };
    };
    return useCreateItem;
};

const createUseDeleteItemHook = (params) => {
    const { useDispatch, slice } = params;
    const useDeleteItem = () => {
        const dispatch = useDispatch();
        const [isLoading, setIsLoading] = useState(false);
        const onDelete = useCallback(async (id) => {
            try {
                setIsLoading(true);
                const dispatchResult = await dispatch(slice.deleteItem({ id }));
                return await unwrapResult(dispatchResult);
            }
            catch (err) {
                // let the app handle error
                throw err;
            }
            finally {
                setIsLoading(false);
            }
        }, [dispatch]);
        return { onDelete, isLoading };
    };
    return useDeleteItem;
};

const createUseGetItemHook = (params) => {
    const { useDispatch, useSelector, slice } = params;
    const sliceName = slice.slice.name;
    const useGetItem = (params) => {
        var _a;
        const memoizedParams = useMemo(() => params, 
        // to avoid redundant re-renders - validate if params really changed
        // eslint-disable-next-line
        [JSON.stringify(params)]);
        const [key, revalidate] = useReducer((x) => x + 1, 0);
        const keyRef = useRef(key);
        const dispatch = useDispatch();
        const [isLoading, setIsLoading] = useState(false);
        const cachedItem = useSelector(selectItemById(sliceName, (_a = memoizedParams.id) !== null && _a !== void 0 ? _a : ''));
        const [savedItem, setSavedItem] = useState();
        useEffect(() => {
            if ((savedItem === null || savedItem === void 0 ? void 0 : savedItem._id) !== memoizedParams.id) {
                setSavedItem(undefined);
            }
        }, [savedItem === null || savedItem === void 0 ? void 0 : savedItem._id, memoizedParams.id]);
        const onGet = useCallback(async () => {
            if (!memoizedParams.id) {
                return;
            }
            try {
                setIsLoading(true);
                // if keys are different - revalidation was called
                const shouldRevalidate = key !== keyRef.current;
                const dispatchResult = await dispatch(slice.fetchItem({ id: memoizedParams.id, revalidate: shouldRevalidate }));
                // sync keys
                keyRef.current = key;
                const result = await unwrapResult(dispatchResult);
                setSavedItem(result);
            }
            finally {
                setIsLoading(false);
            }
        }, [dispatch, key, memoizedParams.id]);
        useEffect(() => {
            onGet();
        }, [onGet]);
        const item = cachedItem !== null && cachedItem !== void 0 ? cachedItem : savedItem;
        const data = memoizedParams.id ? item : undefined;
        return { isLoading, data, revalidate };
    };
    return useGetItem;
};

const createUseInfiniteListHook = (params) => {
    const { useSelector, useDispatch, slice } = params;
    const sliceName = slice.slice.name;
    const useInfiniteList = (params) => {
        const { limit = 20 } = params !== null && params !== void 0 ? params : {};
        const [isLoading, setIsLoading] = useState(true);
        const dispatch = useDispatch();
        const data = useSelector(selectResult(sliceName));
        const total = useSelector(selectTotal(sliceName));
        const skip = useSelector(selectSkip(sliceName));
        const memoizedParams = useMemo(() => params !== null && params !== void 0 ? params : { limit }, 
        // to avoid redundant re-renders - validate if params really changed
        // eslint-disable-next-line
        [JSON.stringify(params)]);
        const loadData = useCallback(async () => {
            try {
                setIsLoading(true);
                await dispatch(slice.fetchList({ params: memoizedParams }));
            }
            finally {
                setIsLoading(false);
            }
        }, [dispatch, memoizedParams]);
        const hasMore = useMemo(() => skip + limit < total, [limit, skip, total]);
        const loadNext = useCallback(() => {
            if (!hasMore) {
                return;
            }
            dispatch(slice.slice.actions.nextPage(limit));
            loadData();
        }, [dispatch, hasMore, limit, loadData]);
        const revalidate = useCallback(async () => {
            dispatch(slice.slice.actions.resetList());
            await loadData();
        }, [dispatch, loadData]);
        useEffect(() => {
            revalidate();
            // automatically revalidate on args change
        }, [revalidate, memoizedParams]);
        return {
            data,
            total,
            isLoading,
            loadNext,
            revalidate,
            skip,
            hasMore,
        };
    };
    return useInfiniteList;
};

const createUseItemsMapHook = (params) => {
    const { useSelector, slice } = params;
    const sliceName = slice.slice.name;
    const useItemsMap = () => {
        const items = useSelector(selectItems(sliceName));
        return items;
    };
    return useItemsMap;
};

const createUseUndeleteItemHook = (params) => {
    const { useDispatch, slice } = params;
    const useUndeleteItem = () => {
        const dispatch = useDispatch();
        const [isLoading, setIsLoading] = useState(false);
        const onUndelete = useCallback(async (id) => {
            try {
                setIsLoading(true);
                const dispatchResult = await dispatch(slice.undeleteItem({ id }));
                const item = await unwrapResult(dispatchResult);
                // item is a parent, then update children
                if (!item.parentId) {
                    await dispatch(slice.fetchVersions({ parentId: item._id }));
                }
            }
            catch (err) {
                // let the app handle error
                throw err;
            }
            finally {
                setIsLoading(false);
            }
        }, [dispatch]);
        return { onUndelete, isLoading };
    };
    return useUndeleteItem;
};

const createUseUpdateItemHook = (params) => {
    const { useDispatch, slice } = params;
    const useUpdateItem = () => {
        const dispatch = useDispatch();
        const [isLoading, setIsLoading] = useState(false);
        const onUpdate = useCallback(async (item) => {
            try {
                setIsLoading(true);
                const dispatchResult = await dispatch(slice.updateItem({ item }));
                // item is a parent, then update children
                if (!item.parentId) {
                    await dispatch(slice.fetchVersions({ parentId: item._id }));
                }
                return await unwrapResult(dispatchResult);
            }
            catch (err) {
                // let the app handle error
                throw err;
            }
            finally {
                setIsLoading(false);
            }
        }, [dispatch]);
        return { onUpdate, isLoading };
    };
    return useUpdateItem;
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const useStatus = () => {
    const [status, setStatus] = useState('idle');
    return useMemo(() => ({
        status,
        setStatus,
        isLoading: status === 'loading',
        isIdle: status === 'idle',
        isError: status === 'error',
        isSuccess: status === 'success',
    }), [status]);
};

const asserError = (error) => {
    const isInstance = error instanceof Error;
    if (isInstance) {
        return isInstance;
    }
    // validate error from other context
    return (typeof error === 'object' && error !== null && 'stack' in error && 'message' in error);
};
const createUseVersionsHook = (params) => {
    const { useSelector, useDispatch, slice } = params;
    const sliceName = slice.slice.name;
    const useVersions = (params, options) => {
        const memoizedParams = useMemo(() => params, 
        // to avoid redundant re-renders - validate if params really changed
        // eslint-disable-next-line
        [JSON.stringify(params)]);
        const optionsRef = useRef(options);
        useEffect(() => {
            // sync options
            optionsRef.current = options;
        }, [options]);
        const _a = useStatus(), { setStatus } = _a, statusState = __rest(_a, ["setStatus"]);
        const [error, setError] = useState();
        const [key, revalidate] = useReducer((x) => x + 1, 0);
        const keyRef = useRef(key);
        const dispatch = useDispatch();
        const data = useSelector(selectVersionItemsWithParent(sliceName, memoizedParams === null || memoizedParams === void 0 ? void 0 : memoizedParams.parentId));
        const onGetVersions = useCallback(async () => {
            var _a, _b, _c;
            const cb = optionsRef.current;
            setError(undefined); // reset status
            if (!(memoizedParams === null || memoizedParams === void 0 ? void 0 : memoizedParams.parentId)) {
                setStatus('idle');
                return;
            }
            try {
                setStatus('loading');
                // if keys are different - revalidation was called
                const shouldRevalidate = key !== keyRef.current;
                const itemDispatchResult = await dispatch(slice.fetchItem({ id: memoizedParams.parentId, revalidate: shouldRevalidate }));
                // get item before all versions
                const item = await unwrapResult(itemDispatchResult);
                const dispatchResult = await dispatch(slice.fetchVersions({
                    parentId: memoizedParams.parentId,
                    revalidate: shouldRevalidate,
                }));
                const result = await unwrapResult(dispatchResult);
                setStatus('success');
                (_a = cb === null || cb === void 0 ? void 0 : cb.onSuccess) === null || _a === void 0 ? void 0 : _a.call(cb, [item, ...((_b = result.data) !== null && _b !== void 0 ? _b : [])], memoizedParams);
            }
            catch (error) {
                const internalError = asserError(error) ? error : new Error('Unknown error');
                setError(internalError);
                setStatus('error');
                (_c = cb === null || cb === void 0 ? void 0 : cb.onError) === null || _c === void 0 ? void 0 : _c.call(cb, internalError);
            }
        }, [dispatch, key, memoizedParams, setStatus]);
        useEffect(() => {
            onGetVersions();
        }, [onGetVersions]);
        return useMemo(() => (Object.assign({ data: (memoizedParams === null || memoizedParams === void 0 ? void 0 : memoizedParams.parentId) ? data : undefined, error,
            revalidate }, statusState)), [data, error, memoizedParams === null || memoizedParams === void 0 ? void 0 : memoizedParams.parentId, statusState]);
    };
    return useVersions;
};

const buildHooks = (params) => {
    return {
        useInfiniteList: createUseInfiniteListHook(params),
        useVersions: createUseVersionsHook(params),
        useCreateItem: createUseCreateItemHook(params),
        useGetItem: createUseGetItemHook(params),
        useUpdateItem: createUseUpdateItemHook(params),
        useDeleteItem: createUseDeleteItemHook(params),
        useUndeleteItem: createUseUndeleteItemHook(params),
        useItemsMap: createUseItemsMapHook(params),
    };
};

export { buildHooks, createVersionisedListSlice };
//# sourceMappingURL=index.js.map
